/**
 * Phone number formatting utilities
 * Formats phone numbers for display and storage
 */

/**
 * Format a phone number for display as (xxx) yyy-zzzz
 * @param value - Raw phone number string (can contain formatting)
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  const cleaned = value.replace(/\D/g, '');

  // Limit to 10 digits
  const limited = cleaned.substring(0, 10);

  // Format as (xxx) yyy-zzzz
  if (limited.length === 0) {
    return '';
  } else if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 6) {
    return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  } else {
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  }
};

/**
 * Remove formatting from phone number to get raw digits
 * @param value - Formatted phone number string
 * @returns Raw phone number with only digits (xxxyyyzzzz)
 */
export const unformatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters to get raw phone number
  return value.replace(/\D/g, '');
};

/**
 * Validate if a phone number is valid (10 digits)
 * @param value - Phone number string (formatted or unformatted)
 * @returns True if valid, false otherwise
 */
export const isValidPhoneNumber = (value: string): boolean => {
  const cleaned = unformatPhoneNumber(value);
  return cleaned.length === 10;
};

/**
 * Format phone number as user types (for input fields)
 * This is useful for onChange handlers
 * @param value - Current input value
 * @returns Formatted phone number
 */
export const formatPhoneInput = (value: string): string => {
  return formatPhoneNumber(value);
};
